import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Monitor, Eye, Layers, TrendingUp, Megaphone, Package } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { FinanceApplicationForm } from "@/components/FinanceApplicationForm";
import kettybotProImg from "@/assets/robots/kettybot-pro-variants-new.jpeg";

const KettyBotPro = () => {
  const [financeOpen, setFinanceOpen] = useState(false);
  const specifications = [
    {
      icon: <Monitor className="w-5 h-5" />,
      label: "Ad Display",
      value: "Customizable Screen"
    },
    {
      icon: <Eye className="w-5 h-5" />,
      label: "Detection",
      value: "360° Fisheye Lenses"
    },
    {
      icon: <Layers className="w-5 h-5" />,
      label: "Trays",
      value: "Smart Detection Trays"
    },
    {
      icon: <Package className="w-5 h-5" />,
      label: "Navigation",
      value: "PUDU VSLAM+"
    }
  ];

  const keyFeatures = [
    {
      icon: <Megaphone className="w-6 h-6" />,
      title: "In-Store Marketing Display",
      description: "Customizable advertisement display transforms your robot into a mobile marketing platform, showcasing promotions and menu items while delivering service."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "360° Fisheye Detection",
      description: "Smart tray detection with 360° fisheye lenses provides comprehensive monitoring of all trays, ensuring accurate delivery tracking and notifications."
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "PUDU VSLAM+ Navigation",
      description: "Marker-less deployment with advanced visual SLAM technology enables rapid setup and flexible reconfiguration without infrastructure modifications."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Seamless Delivery",
      description: "Autonomous load detection automatically recognizes when items are placed or removed, providing real-time status updates and accountability."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Revenue Enhancement",
      description: "Dual-purpose functionality delivers service excellence while generating additional income through targeted in-store advertising and promotions."
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "Flexible Branding",
      description: "Customizable design and display options allow complete brand integration, creating a cohesive customer experience that reinforces your identity."
    }
  ];

  const applications = [
    {
      title: "Restaurants & Cafés",
      description: "Food delivery with menu promotions, daily specials display, and upselling opportunities while maintaining efficient table service."
    },
    {
      title: "Retail Stores",
      description: "Product demonstrations, promotional campaigns, sale announcements, and customer engagement while assisting with merchandise delivery."
    },
    {
      title: "Shopping Malls",
      description: "Directory assistance, promotional displays for multiple tenants, event advertising, and customer guidance throughout the facility."
    },
    {
      title: "Hotels",
      description: "Guest amenities delivery with property services promotion, spa packages advertising, and restaurant menu display."
    },
    {
      title: "Entertainment Venues",
      description: "Concession delivery with event promotion, upcoming shows advertising, and sponsor message display."
    },
    {
      title: "Corporate Offices",
      description: "Cafeteria service with wellness program promotion, company announcements, and internal event advertising."
    }
  ];

  return (
    <>
      <SEO 
        title="KettyBot Pro - Delivery & Advertising Robot | Neil AI"
        description="Versatile delivery and advertising robot with in-store marketing display, 360° smart tray detection, and PUDU VSLAM+ marker-less navigation."
        keywords="KettyBot Pro, delivery robot, advertising robot, marketing robot, restaurant automation, retail robot"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-12 pb-8 md:pt-16">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Robots
            </Link>
          </Button>

          {/* Hero Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="relative bg-white/5 rounded-lg p-8 flex items-center justify-center">
              <img 
                src={kettybotProImg} 
                alt="KettyBot Pro - Delivery & Advertising Robot" 
                className="w-full h-auto max-h-[600px] object-cover rounded-lg"
              />
              <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground font-bold">
                NEW
              </Badge>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <p className="text-primary font-semibold mb-2">Delivery & Advertising Robot</p>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">KettyBot Pro</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 leading-relaxed">
                  Versatile delivery and advertising robot with in-store marketing capabilities, smart tray detection, and marker-less deployment.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <p className="text-xs font-semibold">{spec.label}</p>
                      </div>
                      <p className="text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg"
                  className="glow-gold flex-1"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setFinanceOpen(true)}
                >
                  Apply for Financing
                </Button>
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Marketing Meets Service Excellence</CardTitle>
                  <CardDescription>
                    Transform service delivery into marketing opportunity with integrated advertising
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    KettyBot Pro revolutionizes the concept of service robots by seamlessly integrating delivery functionality with powerful in-store marketing capabilities. The customizable advertisement display transforms each delivery into a marketing opportunity, showcasing promotions, menu items, special offers, and brand messaging while providing efficient service.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Advanced 360° fisheye lens technology provides comprehensive tray monitoring, automatically detecting when items are loaded or removed. This smart detection system ensures delivery accuracy, provides real-time notifications, and creates accountability for both staff and customers.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    PUDU VSLAM+ marker-less navigation enables rapid deployment without infrastructure modifications. Simply drive the robot through your space once to create a map, then it's ready to operate. Flexible reconfiguration adapts to seasonal layouts and temporary changes without additional setup.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {keyFeatures.map((feature, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))] hover:scale-105 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-primary mb-3">{feature.icon}</div>
                        <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8">
                  <h3 className="font-orbitron text-xl font-bold mb-4">Business Impact</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">45%</p>
                      <p className="text-sm">Labor Cost Reduction</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">20%</p>
                      <p className="text-sm">Increased Upsells via Ads</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">250+</p>
                      <p className="text-sm">Daily Deliveries</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specifications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Technical Specifications</CardTitle>
                  <CardDescription>Detailed specifications of KettyBot Pro</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Advertising System</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Display:</span> Customizable advertisement screen</p>
                          <p className="text-sm"><span className="font-semibold">Content:</span> Cloud-based management system</p>
                          <p className="text-sm"><span className="font-semibold">Updates:</span> Remote content scheduling</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Branding:</span> Fully customizable design</p>
                          <p className="text-sm"><span className="font-semibold">Media:</span> Video, images, animations</p>
                          <p className="text-sm"><span className="font-semibold">Analytics:</span> Impression tracking</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Delivery System</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Trays:</span> Multiple smart detection trays</p>
                          <p className="text-sm"><span className="font-semibold">Detection:</span> 360° fisheye lens monitoring</p>
                          <p className="text-sm"><span className="font-semibold">Load Detection:</span> Automatic recognition</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Capacity:</span> High payload capability</p>
                          <p className="text-sm"><span className="font-semibold">Material:</span> Food-grade surfaces</p>
                          <p className="text-sm"><span className="font-semibold">Notifications:</span> Real-time alerts</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Navigation & Operation</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Technology:</span> PUDU VSLAM+ marker-less</p>
                          <p className="text-sm"><span className="font-semibold">Setup:</span> Rapid deployment, no markers</p>
                          <p className="text-sm"><span className="font-semibold">Mapping:</span> Automatic environment learning</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Obstacles:</span> Dynamic avoidance system</p>
                          <p className="text-sm"><span className="font-semibold">Battery:</span> Long runtime, fast charging</p>
                          <p className="text-sm"><span className="font-semibold">Interface:</span> Multi-language support</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Industry Applications</CardTitle>
                  <CardDescription>
                    Versatile deployment across hospitality and retail
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="space-y-2 p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h3 className="font-bold text-lg text-primary">{app.title}</h3>
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="font-orbitron text-2xl font-bold mb-4">Ready to Boost Revenue & Efficiency?</h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Transform your service delivery into a marketing powerhouse with KettyBot Pro. Reduce costs while increasing customer engagement.
                  </p>
                  <Button asChild size="lg" className="glow-gold">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule Your Demo Today
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <FinanceApplicationForm 
        open={financeOpen} 
        onOpenChange={setFinanceOpen}
        robotName="KettyBot Pro"
      />

      <Footer />
    </>
  );
};

export default KettyBotPro;
